<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { usePlayer } from '@/composables/game/player'
import { useTap } from '@/composables/game/tap'
import TapBackImage from '@/assets/images/tap-back-image.png'
import TapFrontImage from '@/assets/images/gifs/duck-money-eye.gif'
import { DateTime } from 'luxon'
import api from '@/services/api'
import type { PlayerStats } from '@/types/shared/player'
import { useConfig } from '@/composables/game/config'
import { debounce } from '@/services/helper'
import { twMerge } from 'tailwind-merge'
import { useAnalytics } from '@/composables/analytics'
import { isiOS } from '@/helpers/browser'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { formatNumberFullTruncate } from '@/helpers/utils'
import FlashIconPNG from '@/components/icons/FlashIconGIF.vue'
import FingerUpGif from '@/assets/images/gifs/finger-up.gif'
import { useOnboardingFingerEmojis } from '@/composables/game/onboarding'
import router from '@/router'
import { RouteName } from '@/routes'
import ShopButton from '@/components/ShopButton.vue'
import PuzzlesButton from '@/components/PuzzlesButton.vue'
import BoostButton from '@/components/BoostButton.vue'
import LimitedPackButton from '@/components/LimitedPackButton.vue'
import BundleButton from '@/components/BundleButton.vue'
import MysteryBoxButton from '@/components/MysteryBoxButton.vue'
import SkinsButton from '@/components/SkinsButton.vue'
import PathwayEventButton from '@/components/PathwayEventButton.vue'
import LiveOpsEventButton from '@/components/LiveOpsEventButton.vue'
import type { PathwayEvent } from '@/types/shared/pathway-event'
import type { LiveOpsEventWithTemplate } from '@/composables/game/live-ops-events'
import { type EcommerceItem, type RewardLiveOp } from '@/types/shared/player-config'
import type { FrontendPathwayEvent } from '@/composables/game/pathway-events'
import LuckyRushButton from '@/components/LuckyRushButton.vue'
import PathwayCompletionChallengeButton from '@/components/PathwayCompletionChallengeButton.vue'
import PuzzleChallengeButton from '@/components/PuzzleChallengeButton.vue'
import ChallengeButton from '@/components/ChallengeButton.vue'
import DailyUpChallengeButton from '@/components/DailyUpChallengeButton.vue'
import MemoryChallengeButton from '@/components/MemoryChallengeButton.vue'
import MinesweeperChallengeButton from '@/components/MinesweeperChallengeButton.vue'
import CollectibleChallengeButton from '@/components/CollectibleChallengeButton.vue'
import LiveOpsChallengeButton from '@/components/LiveOpsChallengeButton.vue'
import { useLiveOpsChallenge } from '@/composables/game/live-ops-challenge'
import PuzzlesCompletionChallengeButton from '@/components/PuzzlesCompletionChallengeButton.vue'
import ExchangeButton from '@/components/ExchangeButton.vue'
import type { Exchange } from '@/types/shared/exchange'
import { useMemoryChallengeModal } from '@/composables/game/memory-challenge-modal'
import { useMinesweeperChallengeModal } from '@/composables/game/minesweeper-challenge-modal'
import { useCollectibleChallengeModal } from '@/composables/game/collectible-challenge-modal'
import { useLiveOpsChallengeModal } from '@/composables/game/live-ops-challenge-modal'
import { useWindowSize } from '@vueuse/core'
import ButtonsScrollContainer from '@/components/ButtonsScrollContainer.vue'
import ArrowRight from '@/components/icons/ArrowRight.vue'
import ClientDealsButton from '@/components/ClientDealsButton.vue'
import webapp from '@/services/webapp'
import BoinkersButton from '@/components/BoinkersButton.vue'
import { BOINKERS_LINK, useBoinkers } from '@/composables/game/boinkers'
import KokomoButton from '@/components/KokomoButton.vue'
import { KOKOMO_LINK, useKokomo } from '@/composables/game/kokomo'

const props = withDefaults(defineProps<{
	hideActions?: boolean
	isCipher?: boolean
	isCipherDone?: boolean
	backImage?: string
	frontImage?: string
	imageWrapperClass?: string
	imageInnerWrapperClass?: string
	tapContainerClass?: string
	backImageClass?: string
	frontImageClass?: string
	fadeImageClass?: string
	hideInnerImage?: boolean
	animate?: boolean
	shop?: {
		hasBundleButton: boolean
		hasShopButton: boolean
		showStarterPack: boolean
		shopCooldown: number
	}
	showLuckyRush?: boolean
	showClientDeals?: boolean
	showBoost?: boolean
	showChallenge?: boolean
	showDailyUpChallenge?: boolean
	showPathwayCompletionChallenge?: boolean
	showMemoryChallenge?: boolean
	showMinesweeperChallenge?: boolean
	showLiveOpsChallenge?: boolean
	showSpecialCollectibleChallenge?: boolean
	showOnboardingEmoji?: boolean
	activeBoostsCount?: number
	activeClientDealsCount?: number
	customText?: string
	showSkins?: boolean
	showMysteryBox?: boolean
	limitedPacks?: EcommerceItem[]
	hasUnseenLimitedPacks?: boolean
	pathwayEvents?: FrontendPathwayEvent[]
	showPuzzleChallenge?: boolean
	showPuzzlesCompletionChallenge?: boolean
	exchange?: Exchange | null
	puzzles?: RewardLiveOp[]
	liveOpsEvents?: LiveOpsEventWithTemplate[]
}>(), {
  hideActions: false,
  isCipher: false,
  isCipherDone: false,
  imageWrapperClass: '',
  imageInnerWrapperClass: '',
  tapContainerClass: '',
  backImageClass: '',
  frontImageClass: '',
  fadeImageClass: '',
  hideInnerImage: false,
  animate: false,
  shop: undefined,
  showBoost: false,
  showOnboardingEmoji: false,
  activeBoostsCount: 0,
  activeClientDealsCount: 0,
  showSkins: false,
  showMysteryBox: false,
  limitedPacks: () => [],
  showLuckyRush: false,
  showClientDeals: false,
  pathwayEvents: () => [],
  puzzles: () => [],
  liveOpsEvents: () => [],
  backImage: () => TapBackImage,
  frontImage: () => TapFrontImage,
  customText: undefined,
  exchange: null,
})

const emit = defineEmits<{
	(e: 'isTapping', isTapping: boolean): void
	(e: 'decipheredLettersChange', letters: string): void
	(e: 'cipherDone', playerStats: PlayerStats): void
	(e: 'onBundleClick'): void
	(e: 'onChallengeClick'): void
	(e: 'onDailyUpChallengeClick'): void
	(e: 'onPathwayCompletionChallengeClick'): void
	(e: 'onLimitedPackClick', limitedPack: EcommerceItem): void
	(e: 'onPathwayEventClick', event: PathwayEvent): void
	(e: 'onPuzzlesClick', puzzleId: number): void
	(e: 'onPuzzleChallengeClick'): void
	(e: 'onPuzzlesCompletionChallengeClick'): void
	(e: 'onExchangeClick', exchange: Exchange): void
	(e: 'onLiveOpsEventClick', event: LiveOpsEventWithTemplate): void
	(e: 'onShopClick'): void
}>()

type TapOptions = { long: boolean } | undefined

const { player } = usePlayer()
const { cipherConfig } = useConfig()
const { tap: _tap } = useTap()
const { addHapticFeedback } = useHapticFeedback()
const { width, height } = useWindowSize()

const showTapOverlay = ref(false)
const isTapping = ref(false)
const tapHorizontalSide = ref<'left' | 'right'>('left')
const tapContainer = ref<HTMLElement | null>(null)
const { triggerEvent } = useAnalytics()
const { canShowOnboardingEmoji, showOnboardingEmojiTapSection } = useOnboardingFingerEmojis()
const { openMemoryChallengeModal } = useMemoryChallengeModal()
const { openMinesweeperChallengeModal } = useMinesweeperChallengeModal()
const { openCollectibleChallengeModal } = useCollectibleChallengeModal()
const { openLiveOpsChallengeModal } = useLiveOpsChallengeModal()
const { activeLeaderboards } = useLiveOpsChallenge()
const { showBoinkers } = useBoinkers()
const { showKokomo } = useKokomo()

const visibleLeaderboards = computed(() =>
  props.showLiveOpsChallenge ? activeLeaderboards.value : [],
)

// Left section scroll state
const leftSectionRef = ref<HTMLElement | null>(null)
const canScrollLeftUp = ref(false)
const canScrollLeftDown = ref(false)
let leftSectionResizeObserver: ResizeObserver | null = null

const leftSectionMaxHeight = ref(260)
const rightSectionMaxHeight = ref(260)
const leftVisibleCount = ref(4)
const rightVisibleCount = ref(4)
const MAX_VISIBLE_ICONS = 4
const LEFT_SECTION_GAP = 12
const RIGHT_SECTION_GAP = 12
const SIDE_ARROW_OVERHEAD = 24 // down arrow only (20px h-5 + 4px mt-1); up arrow hidden at initial scroll position
const ICON_CONTAINER_PT = 10   // pt-2.5 on the icons container

function calculateSectionHeight(el: HTMLElement | null, gap: number): { height: number, count: number } {
  if (!el) return { height: 260, count: MAX_VISIBLE_ICONS }
  const children = Array.from(el.children) as HTMLElement[]
  if (children.length === 0) return { height: 260, count: 0 }

  const tapContainerHeight = tapContainer.value?.clientHeight ?? 0
  const maxAvailableHeight = tapContainerHeight > 0
    ? tapContainerHeight - SIDE_ARROW_OVERHEAD
    : Infinity

  const maxItems = Math.min(children.length, MAX_VISIBLE_ICONS)
  const needsScrolling = children.length > maxItems

  // When no scrolling needed, try fitting without any arrow overhead
  const availableHeight = needsScrolling
    ? maxAvailableHeight
    : (tapContainerHeight > 0 ? tapContainerHeight : Infinity)

  let totalHeight = ICON_CONTAINER_PT
  let count = 0

  for (let i = 0; i < maxItems; i++) {
    const itemHeight = children[i].offsetHeight
    const addition = itemHeight + (count > 0 ? gap : 0)
    if (totalHeight + addition > availableHeight) break
    totalHeight += addition
    count++
  }

  // Edge case: assumed no scrolling but items don't fit → arrows needed, recalculate
  if (!needsScrolling && count < children.length) {
    totalHeight = ICON_CONTAINER_PT
    count = 0
    for (let i = 0; i < maxItems; i++) {
      const itemHeight = children[i].offsetHeight
      const addition = itemHeight + (count > 0 ? gap : 0)
      if (totalHeight + addition > maxAvailableHeight) break
      totalHeight += addition
      count++
    }
  }

  // Always show at least 1 item
  if (count === 0 && children.length > 0) {
    return { height: children[0].offsetHeight + ICON_CONTAINER_PT, count: 1 }
  }

  // When all items are visible, use full available space to prevent sub-pixel clipping
  if (count >= children.length) {
    const height = availableHeight !== Infinity ? availableHeight : totalHeight
    return { height, count }
  }

  return { height: totalHeight, count }
}

function updateLeftSectionScrollState() {
  const el = leftSectionRef.value
  if (!el) return
  const scrollThreshold = 20
  canScrollLeftUp.value = el.scrollTop > 0
  canScrollLeftDown.value = el.scrollTop + el.clientHeight < el.scrollHeight - scrollThreshold
}

function scrollLeftSectionUp() {
  const el = leftSectionRef.value
  if (!el) return
  const scrollAmount = leftVisibleCount.value > 0 ? leftSectionMaxHeight.value / leftVisibleCount.value : 80
  if (el.scrollTop < scrollAmount * 1.5) {
    el.scrollTo({ top: 0, behavior: 'smooth' })
  } else {
    el.scrollBy({ top: -scrollAmount, behavior: 'smooth' })
  }
}

function scrollLeftSectionDown() {
  const el = leftSectionRef.value
  if (!el) return
  const scrollAmount = leftVisibleCount.value > 0 ? leftSectionMaxHeight.value / leftVisibleCount.value : 80
  const remainingScroll = el.scrollHeight - el.clientHeight - el.scrollTop
  if (remainingScroll < scrollAmount * 1.5) {
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  } else {
    el.scrollBy({ top: scrollAmount, behavior: 'smooth' })
  }
}

function updateLeftSectionMaxHeight() {
  const result = calculateSectionHeight(leftSectionRef.value, LEFT_SECTION_GAP)
  leftSectionMaxHeight.value = result.height
  leftVisibleCount.value = result.count
}

function setupLeftSectionScrollListeners() {
  const el = leftSectionRef.value
  if (!el) return

  el.addEventListener('scroll', updateLeftSectionScrollState)

  leftSectionResizeObserver = new ResizeObserver(() => {
    updateLeftSectionScrollState()
    updateLeftSectionMaxHeight()
  })
  leftSectionResizeObserver.observe(el)

  updateLeftSectionScrollState()
  updateLeftSectionMaxHeight()
}

function cleanupLeftSectionScrollListeners() {
  const el = leftSectionRef.value
  if (el) {
    el.removeEventListener('scroll', updateLeftSectionScrollState)
  }
  leftSectionResizeObserver?.disconnect()
  leftSectionResizeObserver = null
}

// Right section scroll state
const rightSectionRef = ref<HTMLElement | null>(null)
const canScrollUp = ref(false)
const canScrollDown = ref(false)
let rightSectionResizeObserver: ResizeObserver | null = null

function updateRightSectionScrollState() {
  const el = rightSectionRef.value
  if (!el) return
  const scrollThreshold = 20
  canScrollUp.value = el.scrollTop > 0
  canScrollDown.value = el.scrollTop + el.clientHeight < el.scrollHeight - scrollThreshold
}

function scrollRightSectionUp() {
  const el = rightSectionRef.value
  if (!el) return
  const scrollAmount = rightVisibleCount.value > 0 ? rightSectionMaxHeight.value / rightVisibleCount.value : 80
  // If remaining scroll is less than 1.5x scroll amount, go to top
  if (el.scrollTop < scrollAmount * 1.5) {
    el.scrollTo({ top: 0, behavior: 'smooth' })
  } else {
    el.scrollBy({ top: -scrollAmount, behavior: 'smooth' })
  }
}

function scrollRightSectionDown() {
  const el = rightSectionRef.value
  if (!el) return
  const scrollAmount = rightVisibleCount.value > 0 ? rightSectionMaxHeight.value / rightVisibleCount.value : 80
  const remainingScroll = el.scrollHeight - el.clientHeight - el.scrollTop
  // If remaining scroll is less than 1.5x scroll amount, go to bottom
  if (remainingScroll < scrollAmount * 1.5) {
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  } else {
    el.scrollBy({ top: scrollAmount, behavior: 'smooth' })
  }
}

function updateRightSectionMaxHeight() {
  const result = calculateSectionHeight(rightSectionRef.value, RIGHT_SECTION_GAP)
  rightSectionMaxHeight.value = result.height
  rightVisibleCount.value = result.count
}

function setupRightSectionScrollListeners() {
  const el = rightSectionRef.value
  if (!el) return

  el.addEventListener('scroll', updateRightSectionScrollState)

  rightSectionResizeObserver = new ResizeObserver(() => {
    updateRightSectionScrollState()
    updateRightSectionMaxHeight()
  })
  rightSectionResizeObserver.observe(el)

  updateRightSectionScrollState()
  updateRightSectionMaxHeight()
}

function cleanupRightSectionScrollListeners() {
  const el = rightSectionRef.value
  if (el) {
    el.removeEventListener('scroll', updateRightSectionScrollState)
  }
  rightSectionResizeObserver?.disconnect()
  rightSectionResizeObserver = null
}

onMounted(() => {
  setupLeftSectionScrollListeners()
  setupRightSectionScrollListeners()
})

onUnmounted(() => {
  cleanupLeftSectionScrollListeners()
  cleanupRightSectionScrollListeners()
})

// Recalculate side sections when viewport height changes
watch(height, () => {
  updateLeftSectionMaxHeight()
  updateRightSectionMaxHeight()
})

//for cipher:
const taps = ref<('-' | '.')[]>([])
const decipheredLetters = ref('')
const isCheckingCipher = ref(false)
const cipherSize = computed(() => cipherConfig.value.size)

function createFloatingNumber(x: number, y: number, text: string, longTap: boolean, isCustomText = false) {
  const numberElement = document.createElement('div')
  const backgroundElement = document.createElement('div')
  const textElement = document.createElement('div')

  // Apply styles to the outer div
  numberElement.className = 'floating-number'
  numberElement.style.left = x + 'px'
  numberElement.style.top = (y + window.scrollY) + 'px'
  if (!isiOS()) {
    numberElement.style.mixBlendMode = 'difference'
  }

  // Background element
  backgroundElement.className = 'floating-background'

  // Text element
  textElement.className = 'floating-number-text'
  textElement.style.fontSize = props.isCipher ? (longTap ? '40px' : '66px') : '24px'
  textElement.innerText = props.isCipher ? (longTap ? '_' : '.') : (isCustomText ? text : `+${text}`)

  // Append elements in the correct order
  numberElement.appendChild(backgroundElement)
  numberElement.appendChild(textElement)

  // Append the numberElement to the body
  document.body.appendChild(numberElement)

  // Remove element after animation ends
  numberElement.addEventListener('animationend', function() {
    numberElement.remove()
  })
}

const enableScroll = debounce(() => {
  isTapping.value = false
}, 400)

function disableScroll() {
  isTapping.value = true
  enableScroll()
}

function getTapPositions(event: TouchEvent | MouseEvent) {
  if ('x' in event) {
    return [{
      x: event.x,
      y: event.y,
    }]
  } else {
    const positions: { x: number, y: number }[] = []
    for (const touch of event.changedTouches) {
      positions.push({
        x: touch.clientX,
        y: touch.clientY,
      })
    }

    return positions
  }
}

const clickableButtons = ['tap_section_actions', 'left-buttons-container', 'left-section-wrapper', 'right-buttons-container', 'right-section-wrapper', 'bottom-buttons-container']

function checkForTapPrevent(event: TouchEvent) {
  const targetElement = (event.srcElement ?? event.target) as Element
  if (!targetElement) return null
  for (const button of clickableButtons) {
    if (targetElement.closest(`#${button}`)) {
      return button
    }
  }
  return null
}

async function tap(event: TouchEvent, options?: TapOptions) {
  showOnboardingEmojiTapSection.value = false
  let clickedElement = checkForTapPrevent(event)
  if (clickedElement) {
    event.stopPropagation()
    if (clickedElement === 'bundle_button') {
      event.preventDefault()
      emit('onBundleClick')
    }
    return
  }
  showTapOverlay.value = true
  if (event.cancelable) {
    event.stopPropagation()
    event.preventDefault()
  }

  const positions = getTapPositions(event)
  disableScroll()
  const validTapCount = _tap(positions.length)
  if (validTapCount === 0) return

  addHapticFeedback('medium')
  if (positions[0]) {
    setTapHorizontalSide(positions[0].x)
  }

  for (const position of positions.slice(0, validTapCount)) {
    const isLongTap = options?.long ?? false
    if (props.isCipher) {
      taps.value.push(isLongTap ? '-' : '.')
    }

    // Determine if custom text should be displayed instead
    const showCustomText = props.customText ? Math.random() < 0 : false // 30% chance to display the given custom text
    createFloatingNumber(position.x, position.y, showCustomText && props.customText ? props.customText : formatNumberFullTruncate(player.value.tap.earnPerTap * player.value.tap.coinPerUnit), isLongTap, showCustomText)
  }

  if (!props.isCipher || props.isCipherDone) return
  await checkTapsForCipher()
}

function longTap(event: TouchEvent) {
  tap(event, { long: true })
}

async function checkTapsForCipher() {
  if (taps.value.length > 0 && (typeof cipherSize.value[decipheredLetters.value.length] !== 'undefined') && taps.value.length >= cipherSize.value[decipheredLetters.value.length] && taps.value.length <= 10 && !isCheckingCipher.value) {
    isCheckingCipher.value = true
    try {
      const response = await api.cipher(decipheredLetters.value, taps.value.join(''))
      decipheredLetters.value = response.decipheredLetters
      if (decipheredLetters.value.length) {
        addHapticFeedback()
      }
      if (response.successful) {
        triggerEvent({ event: 'cipher_done' })
        emit('cipherDone', response.playerStats)
      }
    } finally {
      isCheckingCipher.value = false
      taps.value = []
    }
  } else {
    const tapsLength = taps.value.length
    setTimeout(() => {
      if (tapsLength === taps.value.length) {
        taps.value = []
        decipheredLetters.value = ''
      }
    }, 2000)
  }
}

const touchStartDate = ref(DateTime.now())

function press() {
  if (!props.isCipher) return
  touchStartDate.value = DateTime.now()
}

function release(event: TouchEvent) {
  if (!props.isCipher) return
  if (DateTime.now().toJSDate().getTime() > touchStartDate.value.plus({ second: 1 }).toJSDate().getTime()) {
    tap(event, { long: true })
  }
}

function setTapHorizontalSide(clickPosition: number) {
  if (tapContainer.value) {
    let rect = tapContainer.value.getBoundingClientRect()
    let x = clickPosition - rect.left // X coordinate of the click relative to the image
    let imageCenter = rect.width / 2
    tapHorizontalSide.value = x < imageCenter ? 'left' : 'right'
  }
}

watch(showTapOverlay, () => {
  if (!showTapOverlay.value) {
    return
  }
  setTimeout(() => showTapOverlay.value = false, 100)
})

watch(isTapping, isTapping => emit('isTapping', isTapping))

watch(decipheredLetters, decipheredLetters => {
  emit('decipheredLettersChange', decipheredLetters)
})
</script>

<template>
  <div class="flex flex-col w-full h-full justify-between pt-4">
    <div
      ref="tapContainer"
      :class="[{'touch-none': isTapping, 'scale-in': animate }, twMerge('flex z-[10] relative justify-between flex-1', tapContainerClass)]"
      v-touch:tap="tap"
      v-touch:longtap="longTap"
      v-touch:press="press"
      v-touch:release="release"
    >
      <img
        v-if="showOnboardingEmoji && canShowOnboardingEmoji && showOnboardingEmojiTapSection"
        class="pointer-events-none absolute right-0 bottom-0 size-[148px] rotate-[-45deg] object-cover z-[1]"
        :src="FingerUpGif"
      >

      <!-- Left section wrapper with scroll arrows -->
      <div
        id="left-section-wrapper"
        class="flex flex-col items-center self-start h-full"
      >
        <!-- Up arrow -->
        <button
          :class="['z-[21] flex h-5 w-full items-center justify-center mb-2', canScrollLeftUp ? 'pointer-events-auto' : 'hidden pointer-events-none']"
          aria-label="Scroll up"
          type="button"
          @click.stop="scrollLeftSectionUp()"
        >
          <ArrowRight class="min-w-[20px] size-5 rotate-[-90deg]" />
        </button>

        <div
          ref="leftSectionRef"
          id="left-buttons-container"
          class="pointer-events-auto z-[20] flex flex-col gap-y-3 pt-2.5 overflow-x-visible overflow-y-auto hide-scrollbar w-[72px] px-4"
          :class="{'-mt-2.5': !canScrollLeftUp }"
          :style="{ maxHeight: leftSectionMaxHeight + 'px' }"
        >
          <ShopButton
            v-if="!!shop && shop.hasShopButton"
            :has-unseen-limited-packs="hasUnseenLimitedPacks"
            @click="addHapticFeedback('light');emit('onShopClick');router.push({name: RouteName.SHOP})"
          />
          <MysteryBoxButton
            v-if="showMysteryBox"
            @click="addHapticFeedback('light');router.push({name: RouteName.MYSTERY_BOX})"
          />
          <LimitedPackButton
            v-for="limitedPack in limitedPacks"
            :key="limitedPack.id"
            :limited-pack="limitedPack"
            @click="addHapticFeedback('light');emit('onLimitedPackClick', limitedPack)"
          />
          <BundleButton
            v-if="!!shop && shop.hasBundleButton"
            :cool-down="shop.shopCooldown"
            :label="shop.showStarterPack ? 'Starter' : 'Turbo Pack'"
            :is-starter="shop.showStarterPack"
            @click="addHapticFeedback('light');emit('onBundleClick')"
          />
          <SkinsButton
            v-if="false && showSkins"
            @click="addHapticFeedback('light');router.push({name: RouteName.SKINS})"
          />
        </div>

        <!-- Down arrow -->
        <button
          :class="['z-[21] flex h-5 w-full items-center justify-center mt-1', canScrollLeftDown ? 'pointer-events-auto' : 'hidden pointer-events-none']"
          aria-label="Scroll down"
          type="button"
          @click.stop="scrollLeftSectionDown()"
        >
          <ArrowRight class="min-w-[20px] size-5 rotate-90" />
        </button>
      </div>
      <div :class="twMerge('cursor-pointer flex items-center w-full rounded-ful flex-1', imageWrapperClass)">
        <div
          class="w-full flex flex-col items-center justify-end"
          :class="showTapOverlay ? (tapHorizontalSide === 'left' ? 'left-transform-image' : 'right-transform-image') : ''"
        >
          <div
            :class="twMerge('h-full flex flex-col items-center justify-end rounded-full', imageInnerWrapperClass)"
          >
            <div class="max-h-full w-full flex items-end justify-center overflow-hidden z-[20]">
              <img
                :class="twMerge('object-contain', backImageClass)"
                :src="backImage"
                :style="{ height: (height - 590) + 'px' }"
              >
            </div>
            <div
              v-if="!hideActions"
              id="tap_section_actions"
              class="w-full flex items-center justify-center -translate-y-2"
              @click.prevent.stop
            >
              <div class="flex items-center gap-x-1">
                <FlashIconPNG class="w-6 h-6" />
                <div class="text-base leading-4 font-semibold flex items-center text-light-white">
                  <span>{{ player.energy.available }}</span>
                  <span>&nbsp;/&nbsp;</span>
                  <span>{{ player.energy.max }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Right section wrapper with scroll arrows -->
      <div
        id="right-section-wrapper"
        class="flex flex-col items-center self-start h-full"
      >
        <!-- Up arrow -->
        <button
          :class="['z-[21] flex h-5 w-full items-center justify-center mb-2', canScrollUp ? 'pointer-events-auto' : 'hidden pointer-events-none']"
          aria-label="Scroll up"
          type="button"
          @click.stop="scrollRightSectionUp()"
        >
          <ArrowRight class="min-w-[20px] size-5 rotate-[-90deg]" />
        </button>

        <div
          ref="rightSectionRef"
          id="right-buttons-container"
          class="pointer-events-auto z-[20] flex flex-col gap-y-3 pt-2.5 overflow-x-visible overflow-y-auto hide-scrollbar w-[72px] px-4"
          :class="{'-mt-2.5': !canScrollUp }"
          :style="{ maxHeight: rightSectionMaxHeight + 'px' }"
        >
          <BoostButton
            v-if="showBoost"
            :counter="activeBoostsCount"
            @click="addHapticFeedback('light');router.push({name: RouteName.BOOST})"
          />
          <LuckyRushButton
            v-if="showLuckyRush"
            container-class="white-border"
            @click="router.push({name: RouteName.GIVEAWAY})"
          />
          <LiveOpsChallengeButton
            v-for="lb in visibleLeaderboards"
            :key="lb.id"
            :leaderboard="lb"
            @click="addHapticFeedback('light');openLiveOpsChallengeModal(lb.id)"
          />
          <PathwayCompletionChallengeButton
            v-if="showPathwayCompletionChallenge"
            @click="$emit('onPathwayCompletionChallengeClick')"
          />
          <CollectibleChallengeButton
            v-if="showSpecialCollectibleChallenge"
            @click="addHapticFeedback('light');openCollectibleChallengeModal()"
          />
          <PuzzleChallengeButton
            v-if="showPuzzleChallenge"
            @click="addHapticFeedback('light');emit('onPuzzleChallengeClick')"
          />
          <PuzzlesCompletionChallengeButton
            v-if="showPuzzlesCompletionChallenge"
            @click="addHapticFeedback('light');emit('onPuzzlesCompletionChallengeClick')"
          />
          <MemoryChallengeButton
            v-if="showMemoryChallenge"
            @click="addHapticFeedback('light');openMemoryChallengeModal()"
          />
          <MinesweeperChallengeButton
            v-if="showMinesweeperChallenge"
            @click="addHapticFeedback('light');openMinesweeperChallengeModal()"
          />
          <DailyUpChallengeButton
            v-if="showDailyUpChallenge"
            @click="addHapticFeedback('light');emit('onDailyUpChallengeClick')"
          />
          <ChallengeButton
            v-if="showChallenge"
            @click="addHapticFeedback('light');emit('onChallengeClick')"
          />
        </div>

        <!-- Down arrow -->
        <button
          :class="['z-[21] flex h-5 w-full items-center justify-center mt-1', canScrollDown ? 'pointer-events-auto' : 'hidden pointer-events-none']"
          aria-label="Scroll down"
          type="button"
          @click.stop="scrollRightSectionDown()"
        >
          <ArrowRight class="min-w-[20px] size-5 rotate-90" />
        </button>
      </div>
    </div>
    <div
      id="bottom-buttons-container"
      class="pointer-events-auto z-[20] flex w-full overflow-y-visible items-center justify-center mb-4 px-2"
    >
      <ButtonsScrollContainer
        :visible-count="6"
        auto-scroll
      >
        <!-- Dropee x Kokomo campaign -->
        <KokomoButton
          v-if="showKokomo"
          @click="addHapticFeedback('light'); webapp.openLink(KOKOMO_LINK)"
        />
        <!-- Live Ops Events -->
        <LiveOpsEventButton
          v-for="liveOpsEvent in liveOpsEvents"
          :key="liveOpsEvent.id"
          :event="liveOpsEvent"
          @click="addHapticFeedback('light'); emit('onLiveOpsEventClick', liveOpsEvent)"
        />
        <!-- Pathways -->
        <PathwayEventButton
          v-for="pathwayEvent in pathwayEvents"
          :key="pathwayEvent.id"
          :id="`pathway_event_button_${pathwayEvent.id}`"
          :pathway-event="pathwayEvent"
          :class="{'hidden': pathwayEvent.hidden}"
          @click="addHapticFeedback('light'); emit('onPathwayEventClick', pathwayEvent)"
        />
        <!-- Jigsaws (Puzzles) -->
        <PuzzlesButton
          v-for="puzzle in puzzles"
          :key="puzzle.id"
          :puzzle="puzzle"
          @click="addHapticFeedback('light');emit('onPuzzlesClick', puzzle.id)"
        />
        <!--				Client Deals-->
        <ClientDealsButton
          v-if="showClientDeals"
          :counter="activeClientDealsCount"
          @click="addHapticFeedback('light');router.push({name: RouteName.CLIENT_DEALS})"
        />
        <ExchangeButton
          v-if="exchange"
          :exchange="exchange"
          @click="addHapticFeedback('light');emit('onExchangeClick', exchange)"
        />
        <!-- Boinkers campaign -->
        <BoinkersButton
          v-if="showBoinkers"
          @click="addHapticFeedback('light'); webapp.openTelegramLink(BOINKERS_LINK)"
        />
      </ButtonsScrollContainer>
    </div>
  </div>
</template>

<style lang="scss">
.floating-number, .floating-cipher {
	@apply fixed z-[20] flex items-center justify-center h-[44px] min-w-[44px];
	animation: floatup 0.7s ease-in;
	font-family: "space grotesk variable", sans-serif;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	pointer-events: none;
	touch-action: none;
}

.floating-background {
	@apply absolute inset-0 rounded-full bg-solid-white/[.12] backdrop-blur-[6px];
	pointer-events: none;
	touch-action: none;
}

.floating-number-text {
	@apply text-light-white z-[1] relative font-semibold;
	mix-blend-mode: difference;
	pointer-events: none;
	touch-action: none;
}

.floating-cipher {
	@apply pb-9;
}

@keyframes floatup {
	0% {
		opacity: 1;
		transform: translatey(0);
	}
	70% {
		opacity: 1;
	}
	100% {
		opacity: 0;
		transform: translatey(-150px);
	}
}

.left-transform-image {
	transform: perspective(1500px) rotatey(-10deg);
}

.right-transform-image {
	transform: perspective(1500px) rotatey(10deg);
}

.fade-circle-overlay {
	background: white;
	mix-blend-mode: overlay;
	filter: blur(24px);
}
</style>